interface CacheData<T> {
    data: T;
    timestamp: number;
}

export class CacheService<T> {
    private cache: T | null = null;
    private readonly storageKey: string;
    private readonly fetchData: (...args: any[]) => Promise<T>;
    private readonly cacheDuration: number;

    constructor(
        fetchData: (...args: any[]) => Promise<T>,
        storageKey: string,
        cacheDuration: number = 12 * 60 * 60 * 1000
    ) {
        this.fetchData = fetchData;
        this.storageKey = storageKey;
        this.cacheDuration = cacheDuration;
    }

    private shouldRefreshCache(): boolean {
        const cached = localStorage.getItem(this.storageKey);
        if (!cached) {  
            return true;
        }
        
        try {
            const cacheData: CacheData<T> = JSON.parse(cached);
            const now = Date.now();
            const cacheAge = now - cacheData.timestamp;
            const needsRefresh = cacheAge > this.cacheDuration;
            return needsRefresh;
        } catch (error) {
            return true;
        }
    }

    private async refreshCache(...args: any[]): Promise<void> {
        try {
            const data = await this.fetchData(...args);
            this.cache = data;
            
            const cacheData: CacheData<T> = {
                data: data,
                timestamp: Date.now()
            };
            
            localStorage.setItem(this.storageKey, JSON.stringify(cacheData));
        } catch (error) {
            throw error;
        }
    }

    // Public method to force a cache refresh
    async forceRefresh(...args: any[]): Promise<T> {
        await this.refreshCache(...args);
        return this.cache as T;
    }

    async getData(...args: any[]): Promise<T> {
        const cached = localStorage.getItem(this.storageKey);
        
        if (cached) {
            try {
                const cacheData: CacheData<T> = JSON.parse(cached);
                this.cache = cacheData.data;

            } catch (error) {
                console.error(`CacheService: Error parsing cached data for ${this.storageKey}`, error);
            }
        } else {
            console.warn(`CacheService: No cached data found for ${this.storageKey}`);
        }

        if (this.shouldRefreshCache()) {
            await this.refreshCache(...args);
        }
        
        if (!this.cache) {
            console.warn(`CacheService: No data available for ${this.storageKey}, returning empty result`);
            return [] as unknown as T;
        }
        
        return this.cache;
    }
} 
